import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getMeter, getTransformer } from '../../api/topology.ts'
import { getMeterSecondaryTrace } from '../../api/secondary.ts'
import { getMeterLinkOpinions } from '../../api/linkEvidence.ts'
import type { GeoJSONPoint } from '../../types/topology.ts'
import { StatusBadge } from './DetailPanel.tsx'
import { LinkOpinionsSection } from './LinkOpinionsSection.tsx'
import { SecondaryConnectionSection } from './SecondaryConnectionSection.tsx'
import { HistorySection } from './HistorySection.tsx'
import { RelationshipBadge } from './RelationshipBadge.tsx'
import { fmtPhase } from '../../utils/format.ts'
import { useAuth } from '../../hooks/useAuth.ts'
import { hasCapability, capabilityTooltip } from '../../utils/capabilities.ts'

/** A 4326 lon/lat GeoJSON point -> {lat,lng}, or null when absent/malformed. */
function pointToLatLng(p?: GeoJSONPoint | null): { lat: number; lng: number } | null {
  if (!p || p.type !== 'Point' || !Array.isArray(p.coordinates)) return null
  const [lng, lat] = p.coordinates
  if (typeof lat !== 'number' || typeof lng !== 'number') return null
  return { lat, lng }
}

interface MeterDetailProps {
  id: number
  onNavigate: (type: string, id: number) => void
  onTraceRequest: (meterId: number) => void
  traceLoading: boolean
  onStartAssignment: (meterId: number) => void
  /** Report the meter's own map coordinates (or null) so the map can draw a
   *  layer-independent selection highlight at its location. */
  onLocate?: (coords: { lat: number; lng: number } | null) => void
  /** Request the map pan to a point. `force` (the "Show on map" button) always
   *  recenters; auto-pan on selection only moves when the point is off-screen. */
  onPan?: (coords: { lat: number; lng: number }, force?: boolean) => void
  /** True when this meter was opened from a search pick — the search flow
   *  already flies the map there, so suppress the redundant auto-pan. */
  isSearchResult?: boolean
}

export function MeterDetail({
  id,
  onNavigate,
  onTraceRequest,
  traceLoading,
  onStartAssignment,
  onLocate,
  onPan,
  isSearchResult,
}: MeterDetailProps) {
  // Reassignment is capability-gated server-side (gis.override on the assign
  // endpoints, Phase 2A) — mirror it here as disabled-with-tooltip, never
  // hidden (UX C5, spec 3.12).
  const { user } = useAuth()
  const canOverride = hasCapability(user?.capabilities, 'gis.override')

  const { data: meter, isLoading } = useQuery({
    queryKey: ['meter', id],
    queryFn: () => getMeter(id),
  })

  // Derived secondary attachment (transformer -> secondary -> meter). Degrades
  // to available:false when no published run covers the meter's feeder.
  const { data: secondaryTrace } = useQuery({
    queryKey: ['meter-secondary-trace', id],
    queryFn: () => getMeterSecondaryTrace(id),
  })

  // The three opinions about this meter's transformer (GIS attribute, pole,
  // secondary network), each scored against the link held today. Read-only.
  const { data: linkOpinions } = useQuery({
    queryKey: ['meter-link-opinions', id],
    queryFn: () => getMeterLinkOpinions(id),
  })

  const meterCoords = pointToLatLng(meter?.location)

  // Fallback location: when the meter carries no geometry we still want to take
  // the operator to its transformer. The nested `transformer` summary has no
  // coordinates, so fetch the transformer detail only in that case.
  const { data: fallbackTransformer } = useQuery({
    queryKey: ['transformer', meter?.transformer?.id],
    queryFn: () => getTransformer(meter!.transformer!.id),
    enabled: !!meter && !meterCoords && !!meter.transformer,
  })
  const transformerCoords = pointToLatLng(fallbackTransformer?.location)
  const panTarget = meterCoords ?? transformerCoords ?? null

  // Publish the meter's own coordinates for the selection highlight ring, and
  // clear it when this panel unmounts (e.g. navigating on to the transformer).
  useEffect(() => {
    onLocate?.(meterCoords)
  }, [meterCoords?.lat, meterCoords?.lng, onLocate])
  useEffect(() => {
    return () => onLocate?.(null)
  }, [onLocate])

  // Auto-pan once per selected meter (skip search picks — the search flow flew
  // us here already). Falls back to the transformer's location when the meter
  // has none. Keyed by id so a meter->meter switch re-arms.
  const autoPannedRef = useRef<number | null>(null)
  useEffect(() => {
    if (isSearchResult) return
    if (!panTarget) return
    if (autoPannedRef.current === id) return
    autoPannedRef.current = id
    onPan?.(panTarget)
  }, [id, isSearchResult, panTarget?.lat, panTarget?.lng, onPan])

  if (isLoading) {
    return <div className="p-4 text-sm text-gray-400">Loading meter...</div>
  }

  if (!meter) {
    return <div className="p-4 text-sm text-red-400">Meter not found</div>
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div>
        <div className="flex items-start justify-between gap-2">
          <div className="text-base font-semibold text-gray-900">
            {meter.meter_number || `Meter #${meter.id}`}
          </div>
          <button
            type="button"
            onClick={() => meterCoords && onPan?.(meterCoords, true)}
            disabled={!meterCoords}
            title={meterCoords ? 'Show on map' : 'No location in GIS'}
            aria-label="Show on map"
            className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border rounded transition-colors bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 21s-6-5.686-6-10a6 6 0 1112 0c0 4.314-6 10-6 10z" />
              <circle cx="12" cy="11" r="2" />
            </svg>
            Show on map
          </button>
        </div>
        {meter.service_address && (
          <div className="text-sm text-gray-500">{meter.service_address}</div>
        )}
        {(meter.city || meter.zip_code) && (
          <div className="text-xs text-gray-400">
            {[meter.city, meter.state, meter.zip_code].filter(Boolean).join(', ')}
          </div>
        )}
      </div>

      {/* Status */}
      <StatusBadge status={meter.verification_status} />

      {/* Key details */}
      <div className="space-y-1.5 text-sm">
        <DetailRow label="GIS ID" value={meter.gis_id} />
        <DetailRow label="eMerge ID" value={meter.emerge_meter_id} />
        <DetailRow label="Phase" value={meter.phase == null ? null : fmtPhase(meter.phase)} />
        <DetailRow label="Service Type" value={meter.service_type} />
      </div>

      {/* Relationships */}
      <div className="border-t border-gray-200 pt-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Topology</p>

        {meter.transformer ? (
          <button
            onClick={() => onNavigate('Transformer', meter.transformer!.id)}
            className="w-full text-left p-2 rounded bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              <span className="text-sm font-medium text-gray-700">
                {meter.transformer.name || meter.transformer.gis_id || `Transformer #${meter.transformer.id}`}
              </span>
              <RelationshipBadge source={meter.relationship_source} />
            </div>
            <div className="text-xs text-gray-400 ml-4">
              {meter.transformer.verification_status}
            </div>
          </button>
        ) : (
          <div className="p-2 rounded bg-orange-50 border border-orange-200 text-sm text-orange-700">
            No transformer linked
            <button
              onClick={() => onStartAssignment(meter.id)}
              disabled={!canOverride}
              title={!canOverride ? capabilityTooltip('gis.override') : undefined}
              className="ml-2 text-xs underline hover:no-underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
            >
              Assign now
            </button>
          </div>
        )}
      </div>

      {/* Link evidence: the three opinions, side by side (Phase 5.4) */}
      <LinkOpinionsSection opinions={linkOpinions} onNavigate={onNavigate} />

      {/* Secondary connection (derived graph) */}
      <SecondaryConnectionSection
        trace={secondaryTrace}
        onNavigate={onNavigate}
      />

      {/* Audit history (UX C4) */}
      <HistorySection entityType="meter" id={meter.id} />

      {/* Actions */}
      <div className="border-t border-gray-200 pt-3 space-y-1.5">
        <button
          onClick={() => onTraceRequest(meter.id)}
          disabled={traceLoading}
          className="w-full px-3 py-1.5 text-sm bg-cyan-50 text-cyan-700 border border-cyan-200 rounded hover:bg-cyan-100 disabled:opacity-50"
        >
          {traceLoading ? 'Tracing...' : 'Trace Upstream'}
        </button>
        {meter.transformer && (
          <button
            onClick={() => onStartAssignment(meter.id)}
            disabled={!canOverride}
            title={!canOverride ? capabilityTooltip('gis.override') : undefined}
            className="w-full px-3 py-1.5 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reassign Transformer
          </button>
        )}
      </div>
    </div>
  )
}

function DetailRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value === null || value === undefined) return null
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-800 font-medium">{String(value)}</span>
    </div>
  )
}
